import Chart from "chart.js/auto";
import React, { useEffect, useRef } from "react";
import {
  HABITAT_COLORS,
  type GameState,
  type Habitat,
  type PlayerKey,
} from "../vowGameLogic";
import { getScoreLabel } from "./scoreMetadata";
import { CARD_CLASS, PANEL_CLASS } from "./uiConstants";

type AnalysisDashboardsProps = {
  gameState: GameState;
};

const AnalysisDashboards: React.FC<AnalysisDashboardsProps> = ({ gameState }) => {
  const historyCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const playerCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const habitatCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const wmCanvasRef = useRef<HTMLCanvasElement | null>(null);

  const historyChartRef = useRef<Chart | null>(null);
  const playerChartRef = useRef<Chart | null>(null);
  const habitatChartRef = useRef<Chart | null>(null);
  const wmChartRef = useRef<Chart | null>(null);

  const destroyAllCharts = () => {
    historyChartRef.current?.destroy();
    historyChartRef.current = null;
    playerChartRef.current?.destroy();
    playerChartRef.current = null;
    habitatChartRef.current?.destroy();
    habitatChartRef.current = null;
    wmChartRef.current?.destroy();
    wmChartRef.current = null;
  };

  useEffect(() => {
    if (gameState.history.length === 0) {
      destroyAllCharts();
      return;
    }

    const labels = gameState.history.map((_, index) => `Act ${index + 1}`);
    const referencePlayers = gameState.history[0].players;

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          ticks: {
            color: "#475569",
          },
          grid: {
            color: "rgba(148, 163, 184, 0.25)",
          },
        },
        y: {
          beginAtZero: true,
          ticks: {
            color: "#475569",
          },
          grid: {
            color: "rgba(148, 163, 184, 0.2)",
          },
        },
      },
      plugins: {
        legend: {
          labels: {
            color: "#334155",
          },
        },
      },
    };

    destroyAllCharts();

    if (historyCanvasRef.current) {
      const scoreKeys = Object.keys(gameState.scores) as Array<
        keyof typeof gameState.scores
      >;
      const scoreColorOverrides: Partial<Record<keyof typeof gameState.scores, string>> = {
        BIO: "#0d9488",
      };
      const datasets = scoreKeys.map((key, idx) => ({
        label: getScoreLabel(key),
        data: gameState.history.map((entry) => entry.scores[key]),
        borderColor: scoreColorOverrides[key] ?? `hsl(${idx * 45}, 70%, 45%)`,
        backgroundColor: "transparent",
        tension: 0.12,
      }));

      historyChartRef.current = new Chart(historyCanvasRef.current, {
        type: "line",
        data: { labels, datasets },
        options: chartOptions,
      });
    }

    if (playerCanvasRef.current) {
      const playerKeys = Object.keys(referencePlayers) as PlayerKey[];
      const playerColorOverrides: Partial<Record<PlayerKey, string>> = {
        hunter: "#dc2626",
        herder: "#f59e0b",
      };
      const datasets = playerKeys.map((playerKey) => ({
        label: referencePlayers[playerKey].name,
        data: gameState.history.map((entry) => entry.players[playerKey].score),
        borderColor: playerColorOverrides[playerKey] ?? referencePlayers[playerKey].color,
        backgroundColor: "transparent",
        tension: 0.12,
      }));

      playerChartRef.current = new Chart(playerCanvasRef.current, {
        type: "line",
        data: { labels, datasets },
        options: chartOptions,
      });
    }

    if (habitatCanvasRef.current) {
      const habitats: Habitat[] = ["R", "P", "E", "B"];
      const habitatLabels: Record<Habitat, string> = {
        R: "Reedbed",
        P: "Meadow",
        E: "Open Water",
        B: "Woodland",
      };
      const datasets = habitats.map((habitat) => ({
        label: habitatLabels[habitat],
        data: gameState.history.map((entry) => entry.habitatCounts[habitat]),
        borderColor: HABITAT_COLORS[habitat],
        backgroundColor: "transparent",
        tension: 0.12,
        fill: false,
      }));

      habitatChartRef.current = new Chart(habitatCanvasRef.current, {
        type: "line",
        data: { labels, datasets },
        options: chartOptions,
      });
    }

    if (wmCanvasRef.current) {
      const wmData = gameState.history
        .map((entry) => Number(entry.wmChoice))
        .slice(1);
      const wmLabels = labels.slice(1);

      wmChartRef.current = new Chart(wmCanvasRef.current, {
        type: "bar",
        data: {
          labels: wmLabels,
          datasets: [
            {
              label: "Global WM Vote",
              data: wmData,
              backgroundColor: "rgba(125, 106, 185, 0.55)",
              borderColor: "rgba(125, 106, 185, 0.85)",
              borderWidth: 1,
            },
          ],
        },
        options: {
          ...chartOptions,
          scales: {
            ...chartOptions.scales,
            y: {
              beginAtZero: true,
              max: 5,
              ticks: {
                stepSize: 1,
                precision: 0,
                color: "#475569",
              },
              grid: {
                color: "rgba(148, 163, 184, 0.2)",
              },
            },
          },
          plugins: {
            legend: { display: false },
          },
        },
      });
    }

    return () => {
      destroyAllCharts();
    };
  }, [gameState]);

  return (
    <section
      className={`${PANEL_CLASS} col-span-1 border-[#cfc4a6] bg-[#f3eedf] p-6 shadow-md sm:col-span-2 lg:col-span-3 xl:col-span-4`}
    >
      <h2 className="text-2xl font-semibold text-slate-800">Analysis & Dashboards</h2>
      <div className="mt-4 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartCard title="Evolution of WM votes">
          <canvas ref={wmCanvasRef} className="h-full w-full" />
        </ChartCard>
        <ChartCard title="Evolution of Player Scores">
          <canvas ref={playerCanvasRef} className="h-full w-full" />
        </ChartCard>
        <ChartCard title="Evolution of Habitat Types">
          <canvas ref={habitatCanvasRef} className="h-full w-full" />
        </ChartCard>
        <ChartCard title="Evolution of Marsh Indicators">
          <canvas ref={historyCanvasRef} className="h-full w-full" />
        </ChartCard>
      </div>
    </section>
  );
};

type ChartCardProps = {
  title: string;
  children: React.ReactNode;
};

const ChartCard: React.FC<ChartCardProps> = ({ title, children }) => (
  <div className={`${CARD_CLASS} border-[#d5ccb8] bg-[#f6f1e6] p-4 shadow-sm`}>
    <h4 className="text-center text-base font-semibold text-slate-800">{title}</h4>
    <div className="mt-3 h-64 rounded-xl bg-[#f8f4e9] p-3 shadow-inner">{children}</div>
  </div>
);

export default AnalysisDashboards;
